import { useEffect, useState, type JSX } from 'react';
import {
  SettingsDrawer,
  Collapsible,
  PasskeysCard,
  accountSection,
  securitySection,
  featureToggleSection,
  type AdminSection,
  type AuditSession,
  type FeatureFlagItem,
  type ViewerTier,
} from '@cca/ui';
import { FLAG_NAMES, type FeatureFlags } from '@cca/corpus-shared';
import { ApiError, api, type SessionInfo } from '../api/client';
import { useApplyFeatures } from './InstanceContext';

const ACCENT = 'var(--color-rubric, #a4262c)'; // Corpus rubric red

const FLAG_LABEL: Record<keyof FeatureFlags, string> = {
  compare: 'Compare — version visual diff',
};

/**
 * Corpus Settings — the estate-standard role-aware drawer (replaces the bespoke
 * FlagsPanel + local LoginHistoryPanel). Account for everyone; the feature-flag
 * kill-switches and the login audit for the registrar (isAdmin → superuser).
 * Flag changes still apply app-wide instantly via useApplyFeatures.
 */
export function CorpusSettings({
  open,
  onClose,
  viewer,
  session,
  onSignOutAll,
}: {
  open: boolean;
  onClose: () => void;
  viewer: ViewerTier;
  session: SessionInfo;
  onSignOutAll: () => void | Promise<void>;
}): JSX.Element {
  const applyFeatures = useApplyFeatures();
  const [flags, setFlags] = useState<FeatureFlags | null>(null);
  const [flagsError, setFlagsError] = useState<string | undefined>(undefined);
  const [sessions, setSessions] = useState<AuditSession[] | null>(null);
  const [sessionsError, setSessionsError] = useState<string | undefined>(undefined);

  // Registrar-only data, fetched lazily on open.
  useEffect(() => {
    if (!open || viewer !== 'superuser') return;
    let alive = true;
    api
      .flags()
      .then(({ flags }) => alive && setFlags(flags))
      .catch(
        (e) => alive && setFlagsError(e instanceof ApiError ? e.message : 'Could not load flags.'),
      );
    api
      .loginHistory()
      .then(({ sessions }) => alive && setSessions(sessions))
      .catch(
        (e) =>
          alive && setSessionsError(e instanceof ApiError ? e.message : 'Could not load sign-ins.'),
      );
    return () => {
      alive = false;
    };
  }, [open, viewer]);

  const onToggle = async (key: string, next: boolean): Promise<void> => {
    const res = await api.setFlag(key as keyof FeatureFlags, next);
    setFlags(res.flags);
    // Apply app-wide immediately so gated entry points reflect the change with
    // no reload, no KV lag (preserves the FlagsPanel behaviour).
    applyFeatures(res.flags);
  };

  const flagItems: FeatureFlagItem[] = flags
    ? FLAG_NAMES.map((name) => ({
        key: name,
        label: FLAG_LABEL[name] ?? name,
        enabled: flags[name],
      }))
    : [];

  const sections: AdminSection[] = [
    accountSection({
      identity: (
        <>
          Signed in as{' '}
          <strong style={{ color: 'var(--color-ink, #111827)' }}>{session.email}</strong>.
        </>
      ),
      onSignOutThisDevice: () => {
        window.location.href = '/auth/logout';
      },
      onSignOutAll,
    }),
    featureToggleSection({
      title: 'Features',
      minRole: 'superuser',
      flags: flagItems,
      onToggle,
      ...(flagsError ? { error: flagsError } : {}),
    }),
    securitySection({
      title: 'Security',
      // minRole defaults to 'user': passkey management is available to any
      // signed-in user (each manages their OWN passkeys); the login audit below
      // stays registrar-only via SecuritySection's internal superuser RoleGate.
      sessions,
      accent: ACCENT,
      passkeys: (
        <Collapsible title="Passkeys — sign in without a code">
          <PasskeysCard apiBase="/api/auth/passkeys" loginHref="/auth/login" hideTitle />
        </Collapsible>
      ),
      ...(sessionsError ? { sessionsError } : {}),
    }),
  ];

  return (
    <SettingsDrawer
      open={open}
      onClose={onClose}
      viewer={viewer}
      sections={sections}
      accent={ACCENT}
    />
  );
}
